import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { landingLinks } from "@/config/landingLinks";
import { BrandLockup } from "@/components/brand/BrandLogo";

const navLinks = landingLinks.navigation.map((link) => ({
  label: link.label,
  to: link.href,
}));

export function PublicNav() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const useLightHeader = ["/features", "/pricing", "/pipeline"].includes(location.pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      className={`fixed left-0 right-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-border bg-background/95 shadow-sm backdrop-blur-md"
          : "bg-transparent"
      }`}
    >
      <div className="container">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandLockup
              theme={scrolled || useLightHeader ? "light" : "dark"}
              markClassName="h-8"
              textClassName="text-lg"
            />
          </Link>

          <div className="hidden items-center gap-6 md:flex">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-sm font-medium transition-colors ${
                    isActive
                      ? scrolled || useLightHeader
                        ? "text-foreground underline decoration-2 underline-offset-4"
                        : "text-white underline decoration-2 underline-offset-4"
                      : scrolled || useLightHeader
                        ? "text-muted-foreground hover:text-foreground"
                        : "text-white/80 hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          <div className="hidden items-center gap-3 md:flex">
            <Button
              variant="outline"
              size="sm"
              className={
                scrolled || useLightHeader
                  ? ""
                  : "border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
              }
              asChild
            >
              <Link to="/login">Log In</Link>
            </Button>
            <Button
              size="sm"
              className={scrolled ? "" : "bg-accent text-accent-foreground hover:bg-accent/90"}
              asChild
            >
              <Link to="/signup">Get Started</Link>
            </Button>
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                className={
                  scrolled || useLightHeader
                    ? ""
                    : "text-white hover:bg-white/10 hover:text-white"
                }
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mt-8 flex flex-col gap-6">
                <BrandLockup markClassName="h-8" textClassName="text-lg" />
                {navLinks.map((link) => {
                  const isActive = location.pathname === link.to;
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setOpen(false)}
                      className={`text-left text-lg font-medium transition-colors ${
                        isActive
                          ? "text-primary underline decoration-2 underline-offset-4"
                          : "text-foreground hover:text-primary"
                      }`}
                    >
                      {link.label}
                    </Link>
                  );
                })}
                <div className="flex flex-col gap-3 border-t border-border pt-6">
                  <Button variant="outline" asChild onClick={() => setOpen(false)}>
                    <Link to="/login">Log In</Link>
                  </Button>
                  <Button asChild onClick={() => setOpen(false)}>
                    <Link to="/signup">Get Started</Link>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}
